import { Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Dialog } from "components/Dialog";
import { CreateDocumentInput, DocumentType, ProcessingStrategy } from "generated-graphql/graphql";
import { useUploadFiles } from "hooks/useDocumentUpload";
import { useUpsertDocuments } from "hooks/useUpsertDocuments";
import compact from "lodash-es/compact";
import pluralize from "pluralize";
import { useAnalyzeDocuments } from "providers/documentAIAnalysis";
import { useDocumentReview } from "providers/documentReview";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useCallback, useEffect, useRef, useState } from "react";
import { FileRejection } from "react-dropzone";
import { DOCUMENTS } from "routes/Customer/Documents";
import { FACILITY_DOCUMENTS } from "routes/Customer/Facility/Documents";

import {
  DocumentFilePicker,
  extensionFromName,
  FullScreenDocumentPreview,
  useDocumentFiles,
} from "./shared";

export type AddEditDocumentWithAIEnrichmentDialogProps = {
  open: boolean;
  title?: string;
  onClose: () => void;

  /** Maximum number of files to upload. Defaults to 10 */
  maxFiles?: number;
  disabled?: boolean;
  tenantId: string;
};

export function AddEditDocumentWithAIEnrichmentDialog(
  props: AddEditDocumentWithAIEnrichmentDialogProps
) {
  const { tenantId, open, title = "Add Document(s)", onClose, maxFiles = 10, disabled } = props;

  const alerts = useAlerts();
  const { createDialog } = useMinimizedDialogs();

  const [dropRejectedFiles, setDropRejectedFiles] = useState<FileRejection[]>([]);

  const [manualFillLoading, setManualFillLoading] = useState(false);
  const [manualFillConfirmationOpen, setManualFillConfirmationOpen] = useState(false);

  const [ingestWithErrorsConfirmationOpen, setIngestWithErrorsConfirmationOpen] = useState(false);

  // Store files ref for state capture (File objects can't be serialized)
  const filesRef = useRef<File[]>([]);

  const {
    files,
    canAddMore,
    handleDrop,
    removeFile,
    previewFile,
    openPreview,
    closePreview,
    previewUrl,
    resetFiles,
    hasPossibleDuplicates,
  } = useDocumentFiles({
    multiple: true,
    maxFiles,
  });

  // Update files ref when files change
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const uploadFiles = useUploadFiles();
  const { upsertDocuments } = useUpsertDocuments({
    refetchQueries: [FACILITY_DOCUMENTS, DOCUMENTS],
  });

  const { queueJob, queueLoading: queueDocumentAIEnrichmentJobLoading } = useAnalyzeDocuments();
  const { setDocumentsToReview } = useDocumentReview();

  const onFillManually = useCallback(
    async (files: File[]) => {
      setManualFillLoading(true);
      try {
        // Upload files
        const keys = await uploadFiles(
          files.map((file) => ({
            file,
            documentData: { tenantId },
          }))
        );
        const storageLinks = compact(keys);
        if (storageLinks.length !== keys.length) {
          throw new Error("Some files failed to upload. Please try again.");
        }

        // Create basic document records without AI enrichment
        const documents = storageLinks.map((storageLink, i) => ({
          tenantId,
          title: files[i].name,
          fileExtension: extensionFromName(files[i].name),
          storageLink,
          documentType: DocumentType.Other,
          isArchived: false,
        }));

        const createdDocuments = await upsertDocuments({
          documents,
          currentSubmissionDocument: false,
        });

        if (createdDocuments && createdDocuments.length > 0) {
          const documentIds = createdDocuments.map((doc) => doc.id);
          setDocumentsToReview(documentIds);
        }
      } catch (error) {
        alerts.error("Something went wrong while uploading documents.", error);
      } finally {
        setManualFillLoading(false);
        setManualFillConfirmationOpen(false);
        onClose();
      }
    },
    [uploadFiles, tenantId, upsertDocuments, alerts, onClose, setDocumentsToReview]
  );

  const onUploadAndAutofill = useCallback(
    async (files: File[]) => {
      try {
        const keys = await uploadFiles(
          files.map((file) => ({
            file,
            documentData: { tenantId },
          }))
        );
        const storageLinks = compact(keys);
        if (storageLinks.length !== keys.length) {
          throw new Error("Some files failed to upload. Please try again.");
        }

        const documentData: CreateDocumentInput[] = storageLinks.map((storageLink, i) => ({
          storageLink,
          title: files[i].name,
          fileExtension: extensionFromName(files[i].name) ?? "",
        }));

        const newJobId = await queueJob({
          documents: documentData,
          tenantId,
          strategy: ProcessingStrategy.ExtractNewEmbedNew,
          refetchQueries: [FACILITY_DOCUMENTS, DOCUMENTS],
        });

        if (!newJobId) {
          throw new Error("Failed to get job ID from queue response");
        }

        // Show the analyzing dialog
        const dialogId = `document-ai-analyzing-${newJobId}`;
        createDialog({
          id: dialogId,
          dialogType: DialogType.DocumentAIAnalyzing,
          props: {
            open: true,
            jobId: newJobId,
            preferOriginalTitle: true,
            dialogId, // Pass dialogId so DocumentAIAnalyzingDialog uses the same ID when minimizing
          },
        });
      } catch (error) {
        alerts.error(
          "Something went wrong while uploading and beginning the document analysis process.",
          error
        );
        resetFiles();
      } finally {
        //close the dialog
        if (onClose) {
          onClose();
        }
      }
    },
    [uploadFiles, tenantId, queueJob, alerts, resetFiles, createDialog, onClose]
  );

  const handleClickAutofill = useCallback(() => {
    if (dropRejectedFiles.length > 0) {
      setIngestWithErrorsConfirmationOpen(true);
      return;
    }

    onUploadAndAutofill(files);
  }, [dropRejectedFiles, onUploadAndAutofill, files]);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          resetFiles();
          onClose();
        }}
        fullWidth
        maxWidth="sm"
        title={title}
        cancelButtonProps={{
          onClick: () => setManualFillConfirmationOpen(true),
          disabled:
            disabled ||
            files.length === 0 ||
            manualFillLoading ||
            queueDocumentAIEnrichmentJobLoading,
          text: "Fill Manually Instead",
        }}
        primaryActionButtonProps={{
          hideStartIcon: true,
          onClick: handleClickAutofill,
          disabled: disabled || files.length === 0 || manualFillLoading,
          saveText: "Upload & Autofill",
          loading: dropRejectedFiles.length > 0 ? undefined : queueDocumentAIEnrichmentJobLoading,
          loadingText: "Uploading...",
        }}
      >
        <DocumentFilePicker
          files={files}
          hasPossibleDuplicates={hasPossibleDuplicates}
          onDrop={handleDrop}
          onRemove={removeFile}
          onPreview={openPreview}
          canAddMore={canAddMore}
          multiple
          maxFiles={maxFiles}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,.pub,.vsd"
          disabled={disabled || manualFillLoading}
          helperText={`Drag and drop or select up to ${maxFiles} files (PDFs, images, or office documents). We'll try to detect the type of each document and fill in the details for you.`}
          dropRejectedFiles={dropRejectedFiles}
          setDropRejectedFiles={setDropRejectedFiles}
        />
      </Dialog>

      {previewFile && (
        <FullScreenDocumentPreview
          open={Boolean(previewFile)}
          onClose={closePreview}
          fileExtension={extensionFromName(previewFile.name)}
          fileUrl={previewUrl}
          title={previewFile?.name}
        />
      )}
      <ConfirmDialog
        open={manualFillConfirmationOpen}
        onClose={() => setManualFillConfirmationOpen(false)}
        onConfirm={() => onFillManually(files)}
        header="Are you sure you want to fill manually?"
        info={`This means we will skip the automatic data ingestion for the ${files.length} document(s) you have attached. Any document details will need to be entered manually instead.`}
        confirmText="Yes, Proceed"
        loading={manualFillLoading}
        disableConfirm={manualFillLoading}
      />

      <ConfirmDialog
        open={ingestWithErrorsConfirmationOpen}
        onClose={() => setIngestWithErrorsConfirmationOpen(false)}
        onConfirm={() => onUploadAndAutofill(files)}
        header={`Invalid ${pluralize("file", dropRejectedFiles.length)} attached`}
        info={
          <>
            <Typography>
              You have attached {dropRejectedFiles.length} invalid{" "}
              {pluralize("file", dropRejectedFiles.length)} which cannot be uploaded.
            </Typography>
            <Typography sx={{ marginTop: 2 }}>
              Would you like to proceed with uploading the rest of your attached documents?
            </Typography>
          </>
        }
        confirmText="Yes, Proceed"
        loading={queueDocumentAIEnrichmentJobLoading}
      />
    </>
  );
}
